import * as React from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

/**
 * MyDateTimePicker — a single combined date+time picker that maps onto the
 * existing { date: Date, time: Date } pair so the data schema stays unchanged.
 *
 * Props:
 *   date:  Date | null            — date portion (year/month/day; H/M = 0)
 *   time:  Date | null            — time portion (any date; uses H/M only)
 *   setValue({ date, time })      — emits the split pair on change
 *   includeTime: boolean (default true)
 *                                 — when false, render a date-only field but
 *                                   still emit { date, time: null }
 *   label, error, helperText, onError, style, minDateTime, minDate, disabled
 */
const splitDayjs = (dayJsValue, includeTime) => {
    if(!dayJsValue) return { date: null, time: null };
    if(dayJsValue.isValid && !dayJsValue.isValid()){
        return { date: new Date(NaN), time: includeTime ? new Date(NaN) : null };
    }
    const date = new Date(dayJsValue.year(), dayJsValue.month(), dayJsValue.date());
    const time = includeTime
        ? new Date(dayJsValue.year(), dayJsValue.month(), dayJsValue.date(), dayJsValue.hour(), dayJsValue.minute())
        : null;
    return { date, time };
}

const toDayjs = (date, time, includeTime) => {
    if(!date) return null;
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    if(includeTime && time && !Number.isNaN(time.getTime())){
        return dayjs(`${y}-${m}-${d}T${time.getHours()}:${time.getMinutes()}`);
    }
    return dayjs(`${y}-${m}-${d}`);
}

export default function MyDateTimePicker(props) {
    const includeTime = props.includeTime !== false;
    const value = toDayjs(props.date, props.time, includeTime);

    const onChange = (newValue) => {
        props.setValue(splitDayjs(newValue, includeTime));
    }

    const sharedSlotProps = {
        textField: {
            fullWidth: true,
            className: 'fancy-mui-picker-field',
            error: props.error,
            helperText: props.helperText,
        },
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            {includeTime ? (
                <DateTimePicker
                    label={props.label}
                    value={value}
                    onChange={onChange}
                    onError={(reason) => props.onError && props.onError(Boolean(reason))}
                    enableAccessibleFieldDOMStructure={false}
                    minDateTime={props.minDateTime}
                    disabled={props.disabled}
                    slotProps={sharedSlotProps}
                    sx={props.style}
                />
            ) : (
                <DatePicker
                    label={props.label}
                    value={value}
                    onChange={onChange}
                    onError={(reason) => props.onError && props.onError(Boolean(reason))}
                    enableAccessibleFieldDOMStructure={false}
                    minDate={props.minDate}
                    disabled={props.disabled}
                    slotProps={sharedSlotProps}
                    sx={props.style}
                />
            )}
        </LocalizationProvider>
    );
}
